/**
 * Selectors for configured service details
 * These selectors extract specific data from the configured service details response
 * and handle both recurring and on-request service types
 */
import { createSelector } from '@reduxjs/toolkit'
import { format } from 'date-fns'
import { capitalize, groupBy } from 'lodash'

import { ALL_DAYS } from 'settings/constants/day'
import { EVENT_INSTANCE_TYPES, RECURRENCE, RECURRENCE_V2 } from 'settings/constants/service'
import { createDateFromString, formatDateStringToFE, formatDateToFEFormatDateFns } from 'utils/date'
import { getMeasureUnit as formatMeasureUnit } from 'utils/get-measure-unit'
import { get } from 'utils/lodash'
import { formatCentsToDollars } from 'utils/price'
import {
  getPricingPeriod as formatPricingPeriod,
  getRecurrenceText,
  isWeeklyRecurrence as getIsWeeklyRecurrence,
  isDailyRecurrence as getIsDailyRecurrence,
} from 'utils/service'

type RecurringServiceEvent = {
  dayOfTheWeek?: string | null
  dayOfTheMonth?: number | string | null
  eventOccurrence?: number | string | null
  customerRequired?: boolean | null
  serviceDate?: string | null
}
type ServiceContainer = { id?: string | null }
type ScheduledEventRow = {
  serviceDate?: string | null
  actionType?: string | null
  configuredServiceEventType?: string | null
  workOrderStatus?: string | null
}
type ScheduledEventsSource = { events?: ScheduledEventRow[]; actions?: unknown[]; isDraft?: boolean }

// Base selectors
const getAccountId = (state: unknown) => get(state, 'accountId', '')
const getAccountName = (state: unknown) => get(state, 'accountName', '')
const getActionType = (state: unknown) => get(state, 'serviceAction.actionType')
const getBillingEndDate = (state: unknown) => get(state, 'billingEndDate', '')
const getBillingStartDate = (state: unknown) => get(state, 'billingStartDate', '')
const getContainers = (state: unknown) => get(state, 'containers', [])
const getFeeTaxList = (state: unknown) => get(state, 'feeTaxList', [])
const getId = (state: unknown) => get(state, 'id', '')
const getIsDraft = (state: unknown) => get(state, 'isDraft', true)
const getLocation = (state: unknown) => get(state, 'location', {})
const getMaterial = (state: unknown) => get(state, 'material')
const getMaterialColor = (state: unknown) => get(state, 'material.color')
const getMaterialType = (state: unknown) => get(state, 'material.materialType')
const getMeasure = (state: unknown) => get(state, 'measure')
const getMeasureUnit = (state: unknown) => get(state, 'measure.unit')
const getMeasureVolume = (state: unknown) => get(state, 'measure.volume')
const getMethodName = (state: unknown) => get(state, 'serviceMethod.methodName')
const getMethodType = (state: unknown) => get(state, 'serviceMethod.methodType')
const getPricedService = (state: unknown) => get(state, 'pricedService', {})
const getPricingPeriod = (state: unknown) => get(state, 'pricingPeriod')
const getPricingPeriodInterval = (state: unknown) => get(state, 'pricingPeriod.periodInterval', '')
const getPricingPeriodPer = (state: unknown) => get(state, 'pricingPeriod.periodPer')
const getPricingZone = (state: unknown) => get(state, 'pricingZone')
const getPricingZoneName = (state: unknown) => get(state, 'pricingZone.zoneName')
const getPurchaseOrderNumber = (state: unknown) => get(state, 'purchaseOrderNumber', '')
const getBillingName = (state: unknown): string => {
  const value = (state as { billingName?: unknown } | null)?.billingName
  return typeof value === 'string' ? value.trim() : ''
}
const getRecurrence = (state: unknown) => get(state, 'recurrence')
const getRecurrenceFrequency = (state: unknown) => get(state, 'recurrenceFrequency', 1)
const getRecurrenceInterval = (state: unknown) => get(state, 'recurrence.recurrenceInterval')
const getRecurrencePer = (state: unknown) => get(state, 'recurrence.recurrencePer')
const getScheduleEndDate = (state: unknown) => get(state, 'endDate', '')
const getScheduleStartDate = (state: unknown) => get(state, 'startDate', '')
const getServiceAction = (state: unknown) => get(state, 'serviceAction')
const getRecurringServiceEvents = (state: unknown) => get(state, 'recurringServiceEvents', [])
const getServiceMethod = (state: unknown) => get(state, 'serviceMethod')
const getServicePriceCents = (state: unknown) => get(state, 'servicePriceCents', 0)
const getServiceStatus = (state: unknown) => get(state, 'status', '')
const getTags = (state: unknown) => get(state, 'tags', {})
const getTaxZone = (state: unknown) => get(state, 'taxZone')
const getTaxZoneName = (state: unknown) => get(state, 'taxZone.zoneName')
const getDaysOfTheWeek = (state: unknown) => {
  const events = get(state, 'recurringServiceEvents', []) as RecurringServiceEvent[]
  return events
    .map(event => event.dayOfTheWeek)
    .filter((day): day is string => Boolean(day))
    .map(day => capitalize(day.toLowerCase()))
}
const getDaysOfTheMonth = (state: unknown) => {
  const events = get(state, 'recurringServiceEvents', []) as RecurringServiceEvent[]
  return events.map(event => event.dayOfTheMonth).filter(Boolean)
}
const getEventOccurrences = (state: unknown) => {
  const events = get(state, 'recurringServiceEvents', []) as RecurringServiceEvent[]
  return events.map(event => event.eventOccurrence).filter(Boolean)
}
const getIsServiceDeactivated = (state: unknown) => get(state, 'deactivate', false)
const getDeactivateOnEndDate = (state: unknown) => get(state, 'deactivateOnEndDate', false)

/**
 * Selects the service deactivated status boolean flag
 * @param {Object} data - The configured service details data
 * @returns {boolean} - Whether the service is deactivated
 */
export const selectIsServiceDeactivated = createSelector([getIsServiceDeactivated], deactivate => deactivate)

/**
 * Selects whether the service should be deactivated on the end date
 * @param {Object} data - The configured service details data
 * @returns {boolean} - Whether the service should be deactivated on the end date
 */
export const selectDeactivateOnEndDate = createSelector([getDeactivateOnEndDate], deactivateOnEndDate => deactivateOnEndDate)

/**
 * Selects the pricedService object
 * @param {Object} data - The configured service details data
 * @returns {Object} - The pricedService object
 */
export const selectPricedService = createSelector([getPricedService], pricedService => pricedService)

/**
 * Selects the service type (Recurring or On Request)
 * @param {Object} data - The configured service details data
 * @returns {string} - The service type
 */
export const selectServiceType = createSelector([selectPricedService], pricedService => get(pricedService, 'serviceType', ''))

/**
 * Selects the start date from the schedule
 * @param {Object} data - The configured service details data
 * @returns {string} - The formatted start date
 */
export const selectStartDate = createSelector([getScheduleStartDate], startDate => formatDateStringToFE(startDate))

/**
 * Selects the start date from the schedule as a Date object
 * @param {Object} data - The configured service details data
 * @returns {Date|null} - The start date as a Date object
 */
export const selectStartDateAsDate = createSelector([getScheduleStartDate], startDate =>
  startDate ? createDateFromString(startDate) : null
)

/**
 * Selects the measure unit
 * @param {Object} data - The configured service details data
 * @returns {string} - The measure unit
 */
export const selectMeasureUnit = createSelector([getMeasureUnit], unit => unit)

/**
 * Selects the measure volume
 * @param {Object} data - The configured service details data
 * @returns {string} - The measure volume
 */
export const selectMeasureVolume = createSelector([getMeasureVolume], volume => volume)

/**
 * Selects the measure label
 * @param {Object} data - The configured service details data
 * @returns {string} - The measure label
 */
export const selectMeasureLabel = createSelector([selectMeasureVolume, selectMeasureUnit], (volume, unit) => {
  if (unit) {
    return [volume, formatMeasureUnit(unit)].join(' ')
  }
  return ''
})

/**
 * Selects the method name
 * @param {Object} data - The configured service details data
 * @returns {string} - The method name
 */
export const selectMethodName = createSelector([getMethodName], methodName => methodName)

/**
 * Selects the method type
 * @param {Object} data - The configured service details data
 * @returns {string} - The method type
 */
export const selectMethodType = createSelector([getMethodType], methodType => methodType)

/**
 * Selects the service type
 * @param {Object} data - The configured service details data
 * @returns {string} - The service type
 */
export const selectActionType = createSelector([getActionType], type => type)

/**
 * Selects the material type
 * @param {Object} data - The configured service details data
 * @returns {string} - The material type
 */
export const selectMaterialType = createSelector([getMaterialType], materialType => materialType)

/**
 * Selects the material color
 * @param {Object} data - The configured service details data
 * @returns {string} - The material color
 */
export const selectMaterialColor = createSelector([getMaterialColor], materialColor => materialColor)

/**
 * Selects the end date from the schedule
 * @param {Object} data - The configured service details data
 * @returns {string} - The formatted end date
 */
export const selectEndDate = createSelector([getScheduleEndDate], endDate => formatDateStringToFE(endDate))

/**
 * Selects the end date from the schedule as a Date object
 * @param {Object} data - The configured service details data
 * @returns {Date|null} - The end date as a Date object
 */
export const selectEndDateAsDate = createSelector([getScheduleEndDate], endDate => createDateFromString(endDate))

/**
 * Selects the billing start date
 * @param {Object} data - The configured service details data
 * @returns {string} - The formatted billing start date
 */
export const selectBillingStartDate = createSelector([getBillingStartDate], startDate => formatDateStringToFE(startDate))

/**
 * Selects the billing start date as a Date object
 * @param {Object} data - The configured service details data
 * @returns {Date|null} - The billing start date as a Date object
 */
export const selectBillingStartDateAsDate = createSelector([getBillingStartDate], startDate => createDateFromString(startDate))

/**
 * Selects the billing end date
 * @param {Object} data - The configured service details data
 * @returns {string} - The formatted billing end date
 */
export const selectBillingEndDate = createSelector([getBillingEndDate], endDate => formatDateStringToFE(endDate))

/**
 * Selects the billing end date as a Date object
 * @param {Object} data - The configured service details data
 * @returns {Date|null} - The billing end date as a Date object
 */
export const selectBillingEndDateAsDate = createSelector([getBillingEndDate], endDate => createDateFromString(endDate))

/**
 * Selects the pricedService recurrence interval
 * @param {Object} data - The configured service details data
 * @returns {string} - The pricedService recurrence interval
 */
export const selectPricedServiceRecurrenceInterval = createSelector([getRecurrenceInterval], recurrenceInterval => recurrenceInterval)

/**
 * Selects the pricedService recurrence per
 * @param {Object} data - The configured service details data
 * @returns {string} - The pricedService recurrence per
 */
export const selectPricedServiceRecurrencePer = createSelector([getRecurrencePer], recurrencePer => recurrencePer)

/**
 * Selects the pricedService recurrence frequency
 * @param {Object} data - The configured service details data
 * @returns {string} - The pricedService recurrence frequency
 */
export const selectPricedServiceRecurrenceFrequency = createSelector([getRecurrenceFrequency], recurrenceFrequency => recurrenceFrequency)

/**
 * Selects the pricedService recurrence text
 * @param {Object} data - The configured service details data
 * @returns {string} - The formatted recurrence text
 */
export const selectPricedServiceRecurrenceText = createSelector(
  [selectPricedServiceRecurrencePer, selectPricedServiceRecurrenceInterval, selectPricedServiceRecurrenceFrequency],
  (per, interval, frequency) => getRecurrenceText(per, capitalize(interval), frequency)
)

/**
 * Selects the pricing zone name
 * @param {Object} data - The configured service details data
 * @returns {string} - The pricing zone name
 */
export const selectPricingZoneName = createSelector([getPricingZoneName], zoneName => zoneName)

/**
 * Selects the purchase order number
 * @param {Object} data - The configured service details data
 * @returns {string} - The purchase order number
 */
export const selectPurchaseOrderNumber = createSelector([getPurchaseOrderNumber], purchaseOrderNumber => purchaseOrderNumber)

/**
 * Selects the service events based on service type
 * @param {Array} events - The configured service details data
 * @returns {Array} - The service events
 */
export const selectRecurringServiceEvents = createSelector([getRecurringServiceEvents], events => events)

/**
 * Selects the service name
 * @param {Object} data - The configured service details data
 * @returns {string} - The service name
 */
export const selectServiceName = createSelector([selectPricedService], pricedService => get(pricedService, 'serviceName', ''))

/**
 * Selects the billing name override set on the configured service
 * @param {Object} data - The configured service details data
 * @returns {string} - The billing name (empty when not set)
 */
export const selectBillingName = createSelector([getBillingName], billingName => billingName)

/**
 * Selects the service price
 * @param {Object} data - The configured service details data
 * @returns {number} - The service price
 */
export const selectServicePrice = createSelector([getServicePriceCents], priceCents => formatCentsToDollars(priceCents))

/**
 * Selects the business line
 * @param {Object} data - The configured service details data
 * @returns {string} - The business line
 */
export const selectBusinessLine = createSelector([selectPricedService], pricedService => get(pricedService, 'businessLine', ''))

/**
 * Selects the business line icon
 * @param {Object} data - The configured service details data
 * @returns {string} - The business line icon
 */
export const selectBusinessLineIcon = createSelector([selectPricedService], pricedService => get(pricedService, 'businessLineIcon', ''))

/**
 * Selects the account name
 * @param {Object} data - The configured service details data
 * @returns {string} - The account name
 */
export const selectAccountName = createSelector([getAccountName], name => name)

/**
 * Selects the account ID
 * @param {Object} data - The configured service details data
 * @returns {string} - The account ID
 */
export const selectAccountId = createSelector([getAccountId], id => id)

/**
 * Selects the location
 * @param {Object} data - The configured service details data
 * @returns {Object} - The location
 */
export const selectLocation = createSelector([getLocation], location => location)

/**
 * Selects the service status
 * @param {Object} data - The configured service details data
 * @returns {string} - The service status
 */
export const selectServiceStatus = createSelector([getServiceStatus], status => status)
/**
 * Memoize if the service status is draft
 * @param {Object} data - The configured service details data
 * @returns {string} - The service status
 */
export const selectIsDraftService = createSelector([getIsDraft], isDraft => isDraft)

/**
 * Selects the fees and taxes
 * @param {Object} data - The configured service details data
 * @returns {Array} - The fees and taxes
 */
export const selectFeeTaxList = createSelector([getFeeTaxList], feeTaxList => feeTaxList)

/**
 * Selects the pricedService ID
 * @param {Object} data - The configured service details data
 * @returns {string} - The pricedService ID
 */
export const selectPricedServiceId = createSelector([selectPricedService], pricedService => get(pricedService, 'id', ''))

/**
 * Selects the pricedService value
 * @param {Object} data - The configured service details data
 * @returns {number} - The pricedService value
 */
export const selectPricedServiceValue = createSelector([selectPricedService], pricedService => get(pricedService, 'serviceValue', 0))
/**
 * Select the tags and maps to the interactive model
 * @param {Object} data - The configured service details data
 * @returns {Object} - The tags object
 */
export const selectServiceTags = createSelector([getId, selectServiceName, getTags], (id, serviceName, tags) => {
  return {
    configuredServiceId: id,
    id,
    tagDetails: {
      forService: get(tags, 'serviceTags', []),
    },
  }
})
/**
 * Select and parse the days of the week selected for the configured service
 * @param {Object} data - The configured service details data
 * @returns {Array} - The days of the week
 */
export const selectDaysOfWeek = createSelector([getRecurringServiceEvents], serviceEvents => {
  if (!Array.isArray(serviceEvents) || serviceEvents.length === 0) return []
  return serviceEvents
    .filter(event => event?.dayOfTheWeek)
    .map(event => {
      return {
        day: capitalize(event.dayOfTheWeek.toLowerCase()),
        variant: 'active',
        isSelected: true,
        customerRequired: event.customerRequired,
      }
    })
})

/**
 * Select and parse the days of the week selected for the configured service
 * @param {Object} data - The configured service details data
 * @returns {Array} - The days of the week
 */
export const selectDaysOfWeekFilledForDisplay = createSelector([getRecurringServiceEvents], (serviceEvents: RecurringServiceEvent[]) => {
  const selectedDaysSet = new Set(
    serviceEvents.filter(event => event?.dayOfTheWeek).map(event => capitalize((event.dayOfTheWeek ?? '').toLowerCase()))
  )
  const requiredDaysSet = new Set(
    serviceEvents.filter(event => event?.customerRequired).map(event => capitalize((event.dayOfTheWeek ?? '').toLowerCase()))
  )
  return ALL_DAYS.map(day => {
    return {
      day,
      variant: selectedDaysSet.has(day) ? 'active' : 'inactive',
      isSelected: selectedDaysSet.has(day),
      customerRequired: requiredDaysSet.has(day),
      isRequired: requiredDaysSet.has(day),
    }
  })
})
/**
 * Select the pricing period text based on the recurrence interval and per
 * @param {Object} data - The configured service details data
 * @returns {string} - The pricing period text
 */
export const selectPricingPeriodText = createSelector([getPricingPeriodInterval, getPricingPeriodPer], (interval, per) => {
  return interval === 'Instance' ? '' : formatPricingPeriod(per, interval)
})
/**
 * Select the container list
 * @param {Object} data - The configured service details data
 * @returns {Array} - The container list
 */
export const selectContainers = createSelector([getContainers], (containers: ServiceContainer[]) => {
  return containers.map((container, index) => {
    if (!container.id) {
      return {
        ...container,
        id: `container ${index + 1}`,
      }
    }
    return container
  })
})

/**
 * Determines if a service is of type "On Request"
 * @param {Object} data - The configured service details data
 * @returns {boolean} - True if the service is of type "On Request", false otherwise
 */
export const selectIsOnRequestService = createSelector([selectServiceType], serviceType => {
  return serviceType === RECURRENCE_V2.ON_REQUEST || serviceType === RECURRENCE.ON_REQUEST
})

/**
 * Determines if a service is of type "Recurring"
 * @param {Object} data - The configured service details data
 * @returns {boolean} - True if the service is of type "Recurring", false otherwise
 */
export const selectIsRecurringService = createSelector([selectServiceType], serviceType => {
  return serviceType === RECURRENCE_V2.RECURRING || serviceType === RECURRENCE.RECURRING
})

/**
 * Selects the service events, sorts them by date, and formats the dates
 * @param {Object} data - The configured service details data
 * @returns {Array} - The sorted and formatted service events
 */
export const selectSortedFormattedServiceEvents = createSelector(
  [selectRecurringServiceEvents, selectIsDraftService, selectIsRecurringService],
  (events, isDraft, isRecurringService) => {
    if (!events || events.length === 0 || isRecurringService) return []
    return [...events]
      .sort((a, b) => {
        if (isDraft) {
          return Number(createDateFromString(b.serviceDate)) - Number(createDateFromString(a.serviceDate))
        }
        return Number(new Date(a.serviceDate)) - Number(new Date(b.serviceDate))
      })
      .map(event => {
        if (isDraft) {
          return {
            ...event,
            ...(event.serviceDate && { formattedDate: format(createDateFromString(event.serviceDate) as Date, 'MM/dd/yyyy') }),
          }
        }
        return {
          ...event,
          formattedDate: format(new Date(event.serviceDate), 'MM/dd/yyyy'),
        }
      })
  }
)

/**
 * Selects the scheduled and open service events, sorts them by date, and formats the dates
 * Also adds day of month and maps event ID to action object
 * @param {Object} data - The configured service details data
 * @returns {Array} - The filtered, sorted, and formatted service events
 */
export const selectScheduledServiceEvents = createSelector(
  [
    (state: ScheduledEventsSource) => state.events,
    (state: ScheduledEventsSource) => state.actions,
    (state: ScheduledEventsSource) => state.isDraft,
  ],
  (events, actions) => {
    if (!Array.isArray(events) || events.length === 0) return []
    const actionsByType = groupBy(actions, 'actionType')

    return events
      .filter(({ serviceDate }) => Boolean(serviceDate))
      .map(event => {
        const { serviceDate, actionType } = event
        const serviceDateObj = serviceDate ? createDateFromString(serviceDate) : null
        const selectedAction = actionType == null ? undefined : actionsByType[actionType]
        return {
          ...selectedAction,
          ...event,
          ...(serviceDateObj && {
            serviceDateObj,
            formattedServiceDate: formatDateToFEFormatDateFns(serviceDateObj),
            dayOfMonth: serviceDateObj?.getDate(),
          }),
        }
      })
      .sort((a, b) => Number(a.serviceDate) - Number(b.serviceDate))
  }
)

/**
 * Selects the business line type
 * @param {Object} data - The configured service details data
 * @returns {string} - The business line type
 */
export const selectBusinessLineType = createSelector([selectBusinessLineIcon], businessLineIcon =>
  businessLineIcon ? businessLineIcon.replace('.svg', '') : ''
)

/**
 * Selects the measure object
 * @param {Object} data - The configured service details data
 * @returns {Object} - The measure object
 */
export const selectMeasure = createSelector([getMeasure], measure => measure)

/**
 * Selects the material object
 * @param {Object} data - The configured service details data
 * @returns {Object} - The material object
 */
export const selectMaterial = createSelector([getMaterial], material => material)

/**
 * Selects the service method object
 * @param {Object} data - The configured service details data
 * @returns {Object} - The service method object
 */
export const selectServiceMethod = createSelector([getServiceMethod], method => method)

/**
 * Selects the service action object
 * @param {Object} data - The configured service details data
 * @returns {Object} - The service action object
 */
export const selectServiceAction = createSelector([getServiceAction], action => action)

/**
 * Selects the pricing zone object
 * @param {Object} data - The configured service details data
 * @returns {Object} - The pricing zone object
 */
export const selectPricingZone = createSelector([getPricingZone], zone => zone)

/**
 * Selects the pricing period object
 * @param {Object} data - The configured service details data
 * @returns {Object} - The pricing period object
 */
export const selectPricingPeriod = createSelector([getPricingPeriod], period => period)

/**
 * Selects the pricing period interval
 * @param {Object} data - The configured service details data
 * @returns {string} - The pricing period interval
 */
export const selectPricingPeriodInterval = createSelector([getPricingPeriodInterval], interval => interval)

/**
 * Selects the pricing period per
 * @param {Object} data - The configured service details data
 * @returns {string} - The pricing period per
 */
export const selectPricingPeriodPer = createSelector([getPricingPeriodPer], per => per)

/**
 * Selects the recurrence object
 * @param {Object} data - The configured service details data
 * @returns {Object} - The recurrence object
 */
export const selectRecurrence = createSelector([getRecurrence], recurrence => recurrence)

/**
 * Selects the recurrence interval
 * @param {Object} data - The configured service details data
 * @returns {string} - The recurrence interval
 */
export const selectRecurrenceInterval = createSelector([getRecurrenceInterval], interval => interval)

/**
 * Selects the recurrence per
 * @param {Object} data - The configured service details data
 * @returns {string} - The recurrence per
 */
export const selectRecurrencePer = createSelector([getRecurrencePer], per => per)

/**
 * Selects the recurrence frequency
 * @param {Object} data - The configured service details data
 * @returns {string} - The recurrence frequency
 */
export const selectRecurrenceFrequency = createSelector([getRecurrenceFrequency], frequency => frequency)

/**
 * Select tax zone
 * @params {Object} data - The configured service details data
 * @returns {Object} - The tax zone
 */
export const selectTaxZone = createSelector([getTaxZone], taxZone => taxZone)

/**
 * Select tax zone name
 * @params {Object} data - The configured service details data
 * @returns {Object} - The tax zone
 */
export const selectTaxZoneName = createSelector([getTaxZoneName], zoneName => zoneName)

/**
 * Select recurring days of the week (supports multiple days)
 * @params {Object} data - The configured service details data
 * @returns {Array} - Array of recurring days of the week
 */
export const selectDayOfTheWeek = createSelector([getDaysOfTheWeek, selectIsRecurringService], (daysOfWeek, isRecurringService) => {
  if (!isRecurringService) return []
  return daysOfWeek
})

/**
 * Select recurring days of the month (supports multiple days)
 * @params {Object} data - The configured service details data
 * @returns {Array} - Array of recurring days of the month
 */
export const selectDayOfTheMonth = createSelector([getDaysOfTheMonth, selectIsRecurringService], (daysOfMonth, isRecurringService) => {
  if (!isRecurringService) return []
  return daysOfMonth
})

/**
 * Select recurring event occurrences (supports multiple occurrences)
 * @params {Object} data - The configured service details data
 * @returns {Array} - Array of recurring event occurrences
 */
export const selectEventOccurrence = createSelector(
  [getEventOccurrences, selectIsRecurringService],
  (eventOccurrences, isRecurringService) => {
    if (!isRecurringService) return []
    return eventOccurrences
  }
)

/**
 * Determines if a service has a weekly recurrence
 * @param {Object} data - The configured service details data
 * @returns {boolean} - True if the service has a weekly recurrence, false otherwise
 */
export const selectIsWeeklyRecurrence = createSelector([selectRecurrenceInterval], recurrenceInterval =>
  getIsWeeklyRecurrence(recurrenceInterval)
)

/**
 * Determines if a service has a daily recurrence
 * @param {Object} data - The configured service details data
 * @returns {boolean} - True if the service has a daily recurrence, false otherwise
 */
export const selectIsDailyRecurrence = createSelector([selectRecurrenceInterval], recurrenceInterval =>
  getIsDailyRecurrence(recurrenceInterval)
)

/**
 * Determines the event instance type based on service properties
 * @param {Object} data - The configured service details data
 * @returns {string|null} - The event instance type or null
 */
export const selectEventInstanceType = createSelector(
  [selectIsRecurringService, selectDayOfTheWeek, selectDayOfTheMonth, selectEventOccurrence],
  (isRecurringService, dayOfTheWeek, dayOfTheMonth, eventOccurrence) => {
    if (!isRecurringService) return null
    if (Array.isArray(dayOfTheWeek) && dayOfTheWeek.length > 0 && Array.isArray(eventOccurrence) && eventOccurrence.length > 0) {
      return EVENT_INSTANCE_TYPES.DAY_OF_WEEK
    }
    if (Array.isArray(dayOfTheMonth) && dayOfTheMonth.length > 0) {
      return EVENT_INSTANCE_TYPES.SPECIFIC_DATE
    }
    return null
  }
)

/**
 * Determines if a service is ready to publish
 * @param {Object} data - The configured service details data
 * @returns {boolean} - True if the service is ready to publish, false otherwise
 */
export const selectReadyToPublish = createSelector(
  [
    selectIsRecurringService,
    selectIsWeeklyRecurrence,
    selectStartDateAsDate,
    selectRecurrenceFrequency,
    selectDaysOfWeek,
    selectEventInstanceType,
    selectIsOnRequestService,
    selectIsDailyRecurrence,
  ],
  (
    isRecurringService,
    isWeeklyRecurrence,
    startDate,
    recurrenceFrequency,
    daysOfWeek,
    eventInstanceType,
    isOnRequestService,
    isDailyRecurrence
  ) => {
    if (isRecurringService) {
      if (isWeeklyRecurrence) {
        // Count active days
        const activeDaysCount = daysOfWeek.length
        return startDate && parseInt(recurrenceFrequency, 10) === activeDaysCount
      }
      // Allow Daily recurrence to publish without an eventInstanceType
      if (isDailyRecurrence) {
        return !!startDate
      }
      return startDate && eventInstanceType
    }
    return !!isOnRequestService
  }
)
